/**
 * Árvore de rotas e o esqueleto autenticado do app.
 *
 * `/entrar` é a única rota pública. Todo o resto vive sob `ProtectedLayout`, que
 * espera a checagem de sessão, redireciona para o login preservando o destino
 * pretendido e, autenticado, monta o trilho de navegação e o conteúdo.
 * `/usuarios` e `/ajustes` exigem papel `admin`.
 */import type { ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Rail } from '@/components/Rail';
import { Spinner } from '@/components/Spinner';
import { useAuth } from '@/hooks/useAuth';
import { Login } from '@/pages/Login';
import { NotFound } from '@/pages/NotFound';
import { Perfil } from '@/pages/Perfil';

import { Painel } from '@/pages/Painel';
import { NovaProposta } from '@/pages/NovaProposta';
import { Propostas } from '@/pages/Propostas';
import { Proposta as PropostaDetalhe } from '@/pages/Proposta';
import { Catalogo } from '@/pages/Catalogo';
import { CatalogoDetalhe } from '@/pages/CatalogoDetalhe';
import { Clientes } from '@/pages/Clientes';
import { ClienteDetalhe } from '@/pages/ClienteDetalhe';
import { Estados } from '@/pages/Estados';
import { Usuarios } from '@/pages/Usuarios';
import { Ajustes } from '@/pages/Ajustes';

function PageLoading() {
  return (
    <div className="page-loading">
      <Spinner label="Carregando…" />
    </div>
  );
}

function RestrictedArea() {
  return (
    <div className="restricted stack">
      <p className="empty-title display">Área restrita</p>
      <p className="muted">
        Esta seção é só para administradores. Se você precisa de acesso, peça a alguém do time
        para te promover em Usuários.
      </p>
    </div>
  );
}

/** Rotas autenticadas: mostra carregamento quieto durante `GET /auth/me`, depois trilho + conteúdo. */
function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoading />;
  if (!user) {
    return <Navigate to="/entrar" replace state={{ from: location.pathname + location.search }} />;
  }

  return (
    <div className="app-shell">
      <Rail />
      <main className="app-main">
        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/** Envolve uma rota para exigir `role === 'admin'`; membro vê "área restrita", nunca tela em branco. */
function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <RestrictedArea />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/entrar" element={<Login />} />

      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Painel />} />
        <Route path="/nova" element={<NovaProposta />} />
        <Route path="/propostas" element={<Propostas />} />
        <Route path="/propostas/:id" element={<PropostaDetalhe />} />
        <Route path="/catalogo" element={<Catalogo />} />
        <Route path="/catalogo/:id" element={<CatalogoDetalhe />} />
        <Route path="/clientes" element={<Clientes />} />
        <Route path="/clientes/:id" element={<ClienteDetalhe />} />
        <Route path="/estados" element={<Estados />} />
        <Route
          path="/usuarios"
          element={
            <AdminOnly>
              <Usuarios />
            </AdminOnly>
          }
        />
        <Route
          path="/ajustes"
          element={
            <AdminOnly>
              <Ajustes />
            </AdminOnly>
          }
        />
        <Route path="/perfil" element={<Perfil />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
